import { useEffect } from 'react';
import { Player } from '../types';

interface UseBotAutoTurnProps {
  players: Player[];
  currentPlayerIndex: number;
  setPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
  endTurn: () => void;
  isBotGame: () => boolean;
  isBotTurn: () => boolean;
  generateBotDice: (difficulty: 'easy' | 'medium' | 'hard') => number[];
  botDifficulty: 'easy' | 'medium' | 'hard';
  gameWon: boolean;
}

export function useBotAutoTurn({
  players,
  currentPlayerIndex,
  setPlayers,
  endTurn,
  isBotGame,
  isBotTurn,
  generateBotDice,
  botDifficulty,
  gameWon,
}: UseBotAutoTurnProps) {
  // Step 1: If it's the bot's turn and dice are all zero, set the dice
  useEffect(() => {
    if (gameWon) return;
    if (isBotGame() && isBotTurn()) {
      const botPlayer = players[currentPlayerIndex];
      const allZero = botPlayer.dice.every((die) => die.value === 0);
      if (allZero) {
        setTimeout(() => {
          setPlayers((prevPlayers) =>
            prevPlayers.map((player, idx) =>
              idx === currentPlayerIndex
                ? {
                    ...player,
                    dice: generateBotDice(botDifficulty).map((v) => ({ value: v, isFrozen: false })),
                  }
                : player
            )
          );
        }, 400);
      }
    }
    // eslint-disable-next-line
  }, [players, currentPlayerIndex, isBotGame, isBotTurn, generateBotDice, botDifficulty, setPlayers, gameWon]);

  // Step 2: If it's the bot's turn and dice are NOT all zero, call endTurn after all animations are done
  useEffect(() => {
    if (gameWon) return;
    if (isBotGame() && isBotTurn()) {
      const botPlayer = players[currentPlayerIndex];
      const allZero = botPlayer.dice.every((die) => die.value === 0);
      if (!allZero) {
        // Calculate total animation time: (number of dice) * (delay per dice) + (spin duration)
        const diceCount = botPlayer.dice.length;
        const perDiceDelay = 600; // ms, matches PlayerCard
        const spinDuration = 1200; // ms, matches Dice spin
        const totalDelay = perDiceDelay * diceCount + spinDuration;
        const timer = setTimeout(() => {
          endTurn();
        }, totalDelay);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line
  }, [players, currentPlayerIndex, isBotGame, isBotTurn, endTurn, gameWon]);
} 